import type { PartnerReservation } from "@/domain/reservation/types";
import type { OfferCancellation, PartnerOffer, SaleWindow } from "./types";

/** The only thing about selling the portal may say on its own: an end the
 *  partner authored and that has already passed closes the sale on the API's
 *  side too. Whether an offer without an authored bound is selling is the API's
 *  answer alone — it comes from defaults (when selling opens, and a close that
 *  depends on the activity's booking mode) that the portal does not hold. */
export function hasSaleClosed(window: SaleWindow, now: Date = new Date()): boolean {
  return window.endsAt !== null && window.endsAt.getTime() <= now.getTime();
}

export type SaleStanding =
  | { kind: "closed"; endsAt: Date }
  | { kind: "closesLater"; endsAt: Date }
  | { kind: "unstated" };

/** The same single fact `hasSaleClosed` rests on, carrying its instant so a
 *  surface can say *when* selling stopped and point at the value that stopped
 *  it. Without an authored end the standing is `unstated`: that answer is the
 *  API's, and the portal has nothing of its own to say. */
export function saleStanding(window: SaleWindow, now: Date = new Date()): SaleStanding {
  if (window.endsAt === null) return { kind: "unstated" };
  return hasSaleClosed(window, now)
    ? { kind: "closed", endsAt: window.endsAt }
    : { kind: "closesLater", endsAt: window.endsAt };
}

export const salesWindowMessage = {
  endPrecedesStart: "Konec prodeje musí být po jeho začátku",
  endOutlivesSlot: "Konec prodeje nesmí být po konci termínu",
} as const;

/** Both ends of selling belong to the business: it may close before the slot
 *  starts, or stay open after it has started. Only two shapes are genuinely
 *  wrong — selling that closes before it opens, and selling that outlives the
 *  slot people are buying into. */
export function salesEndPrecedesStart(
  salesStart: string | null | undefined,
  salesEnd: string | null | undefined,
): boolean {
  if (!salesStart || !salesEnd) return false;
  const startMs = new Date(salesStart).getTime();
  const endMs = new Date(salesEnd).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return false;
  return endMs <= startMs;
}

export function salesEndOutlivesSlot(
  salesEnd: string | null | undefined,
  activityEndTime: string | null | undefined,
): boolean {
  if (!salesEnd || !activityEndTime) return false;
  const endMs = new Date(salesEnd).getTime();
  const activityEndMs = new Date(activityEndTime).getTime();
  if (Number.isNaN(endMs) || Number.isNaN(activityEndMs)) return false;
  return endMs > activityEndMs;
}

export interface SlotHours {
  startTime: Date;
  endTime: Date;
}

const MINUTE_MS = 60_000;

function wallClockMinute(instant: Date): number {
  return Math.floor(instant.getTime() / MINUTE_MS);
}

/** The slot is a save of its own, sent only when the hour really moved — an
 *  edit that leaves the termín where it was stays a single call. Compared to
 *  the minute, because that is all the editor's pickers express: a stored
 *  instant keeps the seconds it was created with, and an exact comparison would
 *  read every untouched termín as moved. */
export function slotHourMoved(current: SlotHours, edited: SlotHours): boolean {
  return (
    wallClockMinute(current.startTime) !== wallClockMinute(edited.startTime) ||
    wallClockMinute(current.endTime) !== wallClockMinute(edited.endTime)
  );
}

export type OfferStage = "scheduled" | "running" | "ended";

export function offerStage(startTime: Date, endTime: Date, now: Date = new Date()): OfferStage {
  if (endTime.getTime() <= now.getTime()) return "ended";
  if (startTime.getTime() <= now.getTime()) return "running";
  return "scheduled";
}

export const capacityMessage = {
  belowOne: "Nabídněte aspoň jedno místo.",
  belowBooked: (bookedCount: number) =>
    `Míst nemůže být míň než ${bookedCount} — tolik jich už je rezervovaných.`,
} as const;

/** Places already sold are the floor: dropping under them would leave someone
 *  holding a reservation to a place that no longer exists. Null = allowed. */
export function capacityRefusal(capacity: number, bookedCount: number): string | null {
  if (!Number.isInteger(capacity) || capacity < 1) return capacityMessage.belowOne;
  if (capacity < bookedCount) return capacityMessage.belowBooked(bookedCount);
  return null;
}

export function priceDiscountPercentage(originalPrice: number, discountedPrice: number): number | null {
  if (originalPrice <= 0 || discountedPrice >= originalPrice) return null;
  return Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
}

export interface OfferDay {
  date: Date;
  offers: PartnerOffer[];
  /** Places taken across the day, a checkout still in flight included — the
   *  same figure `PartnerOffer.reserved` carries, not what was paid for. */
  occupied: number;
  capacity: number;
  expanded: boolean;
}

function startOfDay(instant: Date): Date {
  return new Date(instant.getFullYear(), instant.getMonth(), instant.getDate());
}

function daysApart(from: Date, to: Date): number {
  const DAY_MS = 24 * 60 * 60 * 1000;
  return Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / DAY_MS);
}

/** A partner arrives either to see today or to publish for a later date, so
 *  today and tomorrow open and everything else waits to be asked for. */
export function groupOffersByDay(offers: PartnerOffer[], now: Date = new Date()): OfferDay[] {
  const byDay = new Map<number, PartnerOffer[]>();
  for (const offer of offers) {
    const day = startOfDay(offer.startTime).getTime();
    const sameDay = byDay.get(day);
    if (sameDay) sameDay.push(offer);
    else byDay.set(day, [offer]);
  }

  return [...byDay.entries()]
    .sort(([a], [b]) => a - b)
    .map(([day, sameDay]) => {
      const startsWithin = daysApart(now, new Date(day));
      return {
        date: new Date(day),
        offers: [...sameDay].sort((a, b) => a.startTime.getTime() - b.startTime.getTime()),
        occupied: sameDay.reduce((total, offer) => total + offer.reserved, 0),
        capacity: sameDay.reduce((total, offer) => total + offer.capacity, 0),
        expanded: startsWithin === 0 || startsWithin === 1,
      };
    });
}

/** The API's computed states for a reservation somebody has actually bought:
 *  paid — which reaches the portal as `upcoming` or `finished` — or waiting for
 *  the business's answer, which is authorized money either way. */
const boughtStates = new Set(["upcoming", "finished", "pending_confirmation"]);

export interface OfferSales {
  sold: number;
}

export function tallyOfferSales(
  reservations: PartnerReservation[],
  offerId: number | null,
): OfferSales {
  const bought = reservations.filter(
    (reservation) => reservation.offerId === offerId && boughtStates.has(reservation.state),
  );
  return { sold: bought.length };
}

/** The sold count comes from the reservations request, so until it has answered
 *  the portal does not know it — and an absent answer is not a zero. */
export const unknownSales = "unknown" as const;

export type OfferSalesTally = OfferSales | typeof unknownSales;

/** The offer payload's own `reserved` — every seat the API counts as taken, a
 *  checkout still in flight included. It is the figure the API floors capacity
 *  on, and it arrives with the sleva itself, so the floor is never unknown. */
export function capacityFloor(reservedSeats: number): number {
  return Math.max(1, reservedSeats);
}

export interface OfferEditability {
  time: boolean;
  price: boolean;
  capacityFloor: number;
}

/** A12 is two constraints, not one. A sale fixes the termín and the price —
 *  Stripe split the money on those figures — and only the reservations know of
 *  it, so not knowing closes both. A seat merely held buys neither and spends
 *  only the capacity. */
export function editableFields(
  sales: OfferSalesTally,
  reservedSeats: number,
): OfferEditability {
  const nothingSold = sales !== unknownSales && sales.sold === 0;
  return {
    time: nothingSold,
    price: nothingSold,
    capacityFloor: capacityFloor(reservedSeats),
  };
}

/** Any customer the tally told, refunded or still owes a refund to keeps a row
 *  they can open. The tally only counts what this call touched: rows cancelled
 *  earlier are not in it, which is what `hasReservationHistory` carries. */
export function cancellationReachedCustomers(tally: OfferCancellation): boolean {
  return (
    tally.reservationCount > 0 ||
    tally.keptShareCount > 0 ||
    tally.refundAmountMinor > 0 ||
    tally.refundFailedAmountMinor > 0
  );
}

export function isKeptOnlyForHistory(tally: OfferCancellation): boolean {
  return tally.hasReservationHistory && !cancellationReachedCustomers(tally);
}

/** Deleting the offer cascades every reservation on it away, so only a termín
 *  that neither reached nor ever carried a customer may be deleted. */
export function canDeleteOffer(tally: OfferCancellation): boolean {
  return !cancellationReachedCustomers(tally) && !tally.hasReservationHistory;
}
